import IconClose from '@mui/icons-material/Close';
import { IconButton, SnackbarContent } from '@mui/material';
import Alert, { AlertProps } from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  OptionsObject,
  SnackbarKey,
  SnackbarMessage,
  SnackbarProvider,
  SnackbarProviderProps,
  useSnackbar,
} from 'notistack';
import { useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { themeModeAtom } from '../../../recoil/atoms/themeModeAtom';
import { MaterialThemeProvider } from '../../themes/MaterialThemeProvider';

const useStyles = makeStyles((_theme) => ({
  // '@global': {
  //   '@keyframes Toastify__trackProgress2': {
  //     '0%': {
  //       transform: 'scaleX(1)',
  //     },
  //     '100%': {
  //       transform: 'scaleX(0)',
  //     },
  //   },
  // },

  snackbarProgressBarAlert: {
    alignItems: 'center',
    pointerEvents: 'auto', // override pointer-events: none
  },

  snackbarProgressBarContainer: {
    position: 'relative',
  },

  '@keyframes snackbarKeyFrames': {
    '0%': {
      transform: 'scaleX(1)',
    },
    '100%': {
      transform: 'scaleX(0)',
    },
  },

  snackbarProgressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 5,
    zIndex: 1,
    opacity: 0.7,
    backgroundColor: 'hsla(0,0%,100%,.7)',
    transformOrigin: 'left',
  },

  snackbarProgressBarAnimation: {
    animation: '$snackbarKeyFrames linear 1 forwards',
    animationPlayState: 'running',
    opacity: 1,
  },

  snackbarProgressBarColor: {
    background:
      'linear-gradient(to right, #4cd964, #5ac8fa, #007aff, #34aadc, #5856d6, #ff2d55)',
  },
}));

function SnackbarCloseButton(props: {
  messageKey: string | number | undefined;
}) {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton onClick={() => closeSnackbar(props.messageKey)} size="large">
      <IconClose htmlColor="white" fontSize="small" />
    </IconButton>
  );
}

export interface IAlertWithProgressBar {
  /** Message to display */
  message: SnackbarMessage;
  /** Progress bar animation in milliseconds (should match Snackbars autoHideDuration) [-1 for default] */
  animationDuration?: number;
  /** Props passed directly to Material-UI Alert */
  alertProps?: AlertProps;
  /** whether the message should persist  */
  persist?: boolean;
  /** key used to identify snackbar messages */
  messageKey?: string;
}

const DEFAULT_ANIMATION_DURATION = 5000;

/**
 * Wrapper around Material-UI Alert that adds progress bar
 *
 * @example
 *  <AlertWithProgressBar
 *    message="This is a success message!"
 *    animationDuration={5000}
 *    alertProps={{ severity: 'warning' }}
 *  />
 */
export const AlertWithProgressBar = ({
  message,
  animationDuration = DEFAULT_ANIMATION_DURATION, // notistack hides in 5000ms by default adjust to match or set to zero to persist, -1 to use default
  alertProps = {},
  messageKey,
}: IAlertWithProgressBar) => {
  const classes = useStyles();
  const themeMode = useRecoilValue(themeModeAtom);
  return (
    // Add theme provider since this is rendered outside of App context
    <MaterialThemeProvider themeMode={themeMode}>
      <Alert
        className={classes.snackbarProgressBarAlert}
        variant="filled"
        action={<SnackbarCloseButton messageKey={messageKey} />}
        {...alertProps}
      >
        {message}
      </Alert>
      <main
        className={clsx(
          classes.snackbarProgressBar,
          classes.snackbarProgressBarAnimation
          // classes.snackbarProgressBarColor
        )}
        style={{
          animationDuration:
            animationDuration !== 0
              ? `${
                  animationDuration > 0
                    ? animationDuration
                    : DEFAULT_ANIMATION_DURATION
                }ms`
              : '',
        }}
      ></main>
    </MaterialThemeProvider>
  );
};

export const useSnackbarWithStyles = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const enqueueSnackbarWithStyles = useCallback(
    (
      alertComponentProps: IAlertWithProgressBar,
      snackbarProps?: OptionsObject
    ): SnackbarKey | null => {
      // used to identify snackbar message so can close individually
      const messageKey = uuidv4();
      return alertComponentProps?.message
        ? enqueueSnackbar(
            <AlertWithProgressBar
              messageKey={messageKey}
              animationDuration={
                ['error', 'warning'].includes(
                  alertComponentProps.alertProps?.severity ?? ''
                )
                  ? 0
                  : -1
              }
              {...alertComponentProps}
            />,
            {
              key: messageKey,
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
              autoHideDuration: alertComponentProps.animationDuration,
              variant: alertComponentProps.alertProps?.severity ?? 'default',
              persist:
                alertComponentProps.animationDuration === 0 ||
                ['error', 'warning'].includes(
                  alertComponentProps.alertProps?.severity ?? ''
                ) ||
                alertComponentProps?.persist,
              ...(['error', 'warning'].includes(
                alertComponentProps.alertProps?.severity ?? ''
              ) || alertComponentProps?.persist
                ? {
                    ClickAwayListenerProps: {
                      onClickAway: () => {},
                    },
                  }
                : {}), // do not include if not needed
              ...snackbarProps,
            }
          )
        : null;
    },
    [enqueueSnackbar]
  );
  return {
    enqueueSnackbarWithStyles,
    closeSnackbar,
  };
};

/**
 * Wrapper around SnackbarProvider that provides background close
 *
 * @param props Props passed to SnackbarProvider
 *
 * @example
 *  <SnackbarProviderWithStyles>
 *    <Sample />
 *  </SnackbarProviderWithStyles>
 *
 *  const Sample = () => {
 *    const { enqueueSnackbar } = useSnackbar();
 *
 *    enqueueSnackbar(
 *      <AlertWithProgressBar
 *        message="This is a success message!"
 *        animationDuration={3000}
 *        alertProps={{ severity: 'success' }}
 *      />,
 *      {
 *        variant: 'default',
 *        autoHideDuration: 3000,
 *        anchorOrigin: {
 *          vertical: 'bottom',
 *          horizontal: 'right',
 *        },
 *        ClickAwayListenerProps: {
 *          onClickAway: () => {},
 *        },
 *      }
 *    );
 *    ...
 *  }
 */
export const SnackbarProviderWithStyles = (props: SnackbarProviderProps) => {
  const snackbarRef = useRef<SnackbarProvider>(null);
  const classes = useStyles();
  return (
    <SnackbarProvider
      ref={snackbarRef}
      maxSnack={5}
      onClose={(event, reason, key) => {
        reason === 'clickaway' && snackbarRef.current?.closeSnackbar(key);
      }}
      content={(key: SnackbarKey, content: SnackbarMessage) =>
        typeof content === 'string' && !!content ? (
          <SnackbarContent
            message={content}
            action={<SnackbarCloseButton messageKey={key} />}
          />
        ) : (
          <div className={classes.snackbarProgressBarContainer}>{content}</div>
        )
      }
      {...props}
    >
      {props.children}
    </SnackbarProvider>
  );
};
